import { useId, useState, type InputHTMLAttributes } from 'react';

type NativeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'>;

interface SparkInputProps extends NativeProps {
  label: string;
  /** Field-level validation message rendered under the control. */
  error?: string;
  id?: string;
}

/**
 * The Spark filled text field: a persistent small label above the value, with
 * the example placeholder revealed only while the field has focus.
 */
export function SparkInput({ label, error, id, type = 'text', ...rest }: SparkInputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const [revealed, setRevealed] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword && revealed ? 'text' : type;

  return (
    <>
      <label className={`spark-input${error ? ' spark-input--invalid' : ''}`} htmlFor={inputId}>
        <span className="spark-label">{label}</span>
        <input
          {...rest}
          id={inputId}
          type={inputType}
          className={`spark-input__field${isPassword ? ' spark-input__field--with-addon' : ''}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {isPassword && (
          <button
            type="button"
            className="spark-input__addon"
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            onClick={() => setRevealed((value) => !value)}
            tabIndex={0}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </label>
      {error && (
        <span className="spark-input__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </>
  );
}

function EyeIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M1.8 12S5.4 5.5 12 5.5 22.2 12 22.2 12 18.6 18.5 12 18.5 1.8 12 1.8 12z" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M1.8 12S5.4 5.5 12 5.5c1.6 0 3 .4 4.2 1M22.2 12s-3.6 6.5-10.2 6.5c-1.7 0-3.2-.4-4.4-1.1" />
      <path d="M9.8 9.9a3.2 3.2 0 004.4 4.5" />
      <path d="M3 3l18 18" />
    </svg>
  );
}
